import { makeBetterEnum } from './betterEnum';

export const FootprintExportGenerationPath = makeBetterEnum(
  'Legacy',
  'Regenerate',
  'Standard'
);
export type FootprintExportGenerationPath =
  (typeof FootprintExportGenerationPath)[keyof typeof FootprintExportGenerationPath];

export const FOOTPRINT_EXPORT_BASE_FIELDS = [
  'categoryId',
  'subcategoryId',
  'month',
  'description',
  'locationCountry',
  'locationCity',
  'buildingName',
  'vendor',
  'rawVendor',
  'inputQuantity',
  'inputUnit',
  'convertedQuantity',
  'convertedUnit',
];

export const FootprintExportStatus = makeBetterEnum(
  'Processing',
  'Error',
  'Complete'
);
export type FootprintExportStatus =
  (typeof FootprintExportStatus)[keyof typeof FootprintExportStatus];

/**
 * Lowercase status values used by the public API schemas.
 */
export const FootprintExportApiStatus = makeBetterEnum(
  'processing',
  'error',
  'complete'
);
export type FootprintExportApiStatus =
  (typeof FootprintExportApiStatus)[keyof typeof FootprintExportApiStatus];

// can't use betterEnum because it doesn't play well with Temporal types
export enum FootprintExportOutputType {
  HumanReadable = 'humanReadable',
  MachineReadable = 'machineReadable',
}
export enum FootprintExportOutputFileFormat {
  Csv = 'csv',
  Parquet = 'parquet',
}
export enum LegacyFootprintExportOutputFileFormat {
  SingleCsv = 'singleCsv',
  CsvBundle = 'csvBundle',
  Parquet = 'parquet',
}

export const EMISSIONS_TYPES_FOR_FOOTPRINT_EXPORT = [
  'market',
  'location',
  'co2',
  'biogenicCo2',
  'biogenicCo2Location',
  'co2Location',
  'ch4',
  'n2o',
  'hfcs',
  'pfcs',
  'sf6',
  'nf3',
  'nonKyotoGases',
] as const;

const EmissionsTypeForFootprintExport = makeBetterEnum(
  ...EMISSIONS_TYPES_FOR_FOOTPRINT_EXPORT
);
export type EmissionsTypeForFootprintExport =
  (typeof EmissionsTypeForFootprintExport)[keyof typeof EmissionsTypeForFootprintExport];

export const EMISSIONS_TYPE_DISPLAY_NAMES_FOR_FOOTPRINT_EXPORT: Record<
  EmissionsTypeForFootprintExport,
  string
> = {
  market: 'Market',
  location: 'Location',
  co2: 'CO₂',
  biogenicCo2: 'Biogenic CO₂',
  biogenicCo2Location: 'Location-based Biogenic CO₂',
  co2Location: 'Location-based CO₂',
  ch4: 'CH₄',
  n2o: 'N₂O',
  hfcs: 'HFCs',
  pfcs: 'PFCs',
  sf6: 'SF₆',
  nf3: 'NF₃',
  nonKyotoGases: 'Non-Kyoto gases',
} as const;

export const EMISSIONS_TYPE_FIELD_PREFIXES = {
  market: '',
  location: 'allocation_location_',
  co2: 'co2_',
  biogenicCo2: 'biogenic_co2_',
  biogenicCo2Location: 'biogenic_co2_location_',
  co2Location: 'co2_location_',
  ch4: 'ch4_',
  n2o: 'n2o_',
  hfcs: 'hfcs_',
  pfcs: 'pfcs_',
  sf6: 'sf6_',
  nf3: 'nf3_',
  nonKyotoGases: 'non_kyoto_gases_',
} as const satisfies Record<EmissionsTypeForFootprintExport, string>;

export type EmissionsFactorFieldPrefix =
  (typeof EMISSIONS_TYPE_FIELD_PREFIXES)[keyof typeof EMISSIONS_TYPE_FIELD_PREFIXES];

export const PER_VARIABLE_EMEF_ALIAS_PREFIX = 'emef_for_';

const EmissionsFactorFieldForFootprintExport = makeBetterEnum(
  'EmissionsModelEfName',
  'EmissionsModelEfValue',
  'EmissionsModelEfUnit',
  'EmissionsModelEfSourceName',
  'EmissionsModelEfSourceNotes',
  'EmissionsModelEfSourceUrl'
);
export type EmissionsFactorFieldForFootprintExport =
  (typeof EmissionsFactorFieldForFootprintExport)[keyof typeof EmissionsFactorFieldForFootprintExport];

export const EMISSIONS_FACTOR_FIELD_DISPLAY_NAMES_FOR_FOOTPRINT_EXPORT: Record<
  EmissionsFactorFieldForFootprintExport,
  string
> = {
  EmissionsModelEfName: 'emissions factor name',
  EmissionsModelEfValue: 'emissions factor value',
  EmissionsModelEfUnit: 'emissions factor unit',
  EmissionsModelEfSourceName: 'emissions factor source name',
  EmissionsModelEfSourceNotes: 'emissions factor source notes',
  EmissionsModelEfSourceUrl: 'emissions factor source URL',
} as const;

export const ACTIVITY_DATA_EXPORT_PREFIX = 'activity_data_';

export const FOOTPRINT_EXPORT_PER_CSV_FILE_SIZE_MB = 250;

export const MACHINE_READABLE_EXPORT_SCHEMAS_DIRECTORY = 'schemas';

export const CUSTOM_COLUMN_DISPLAY_NAME_SUFFIX = '(Custom)';
export const ORG_UNIT_DISPLAY_NAME_SUFFIX = '(Organizational Unit)';

export const MACHINE_READABLE_EXPORT_DATA_DIRECTORY = 'data';
export const MACHINE_READABLE_EXPORT_LOOKUPS_DIRECTORY = 'lookups';

/**
 * The current schema version for machine-readable footprint exports.
 * Changing this value might be a BREAKING CHANGE to the public API.
 * See the test in renderFootprintExportWithActivityData.test.ts for instructions.
 */
export const FOOTPRINT_EXPORT_SCHEMA_VERSION = '2026-04-01.01';

/**
 * Default schema version used as a fallback when rendering exports that
 * were created before export_schema_version was stored in the database.
 * This value should never change.
 */
export const FOOTPRINT_EXPORT_SCHEMA_VERSION_DEFAULT = '2026-04-01.01';

/**
 * The oldest snapshot creation date for which machine-readable exports are
 * supported. Snapshots created before this date cannot be exported in
 * machine-readable format.
 */
export const MACHINE_READABLE_EXPORT_OLDEST_SUPPORTED_SNAPSHOT_DATE = new Date(
  '2023-01-01T00:00:00Z'
);

/**
 * Allow-list of errorParams keys that may be returned in the public API.
 * Any params not in this list are stripped before the response is sent.
 */
export const USER_VISIBLE_ERROR_DATA_KEYS: ReadonlyArray<string> = [
  'cause',
  'filterType',
  'filters',
  'filtersJson',
  'footprintEmissionsKind',
  'interval',
  'outputFormat',
  'outputType',
  'parsed',
  'zodError',
];
